import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { Leva } from "leva";
import { UI } from "./components/UI";
import { Experience } from "./components/Experience";
import { ChatProvider } from "./hooks/useChat";
import styles from './styles.module.css';
import Webcam from "react-webcam";
import { useRef } from "react";
import { AppDispatch } from "../../../../store";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { candidateInterviewBot, candidateUploadS3 } from "./CandidateInterviewBotAction";
import { setRecognizing } from "./CandidateInterviewBotSlice";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Avatar } from "primereact/avatar";
import { candidateRetrieveInterview } from "../BeginInterview/CandidateInterviewAction";
import { InterviewStatusEnum } from "../Chatbot/interface";

interface InterviewBotProps {
}

const InterviewBot = (props: InterviewBotProps) => {
  const { } = props
  const history = useHistory() 
  const { interviewId } = useParams<{ interviewId: string }>()

  // retrieve data from redux
  const dispatch = useDispatch<AppDispatch>()
  const { interview } = useSelector((state: any) => state.candidateInterview)
  const { speechResponse, loading, playing, started, ended, recognizing } = useSelector((state: any) => state.candidateInterviewBot)

  // webcam and recording controls
  const videoConstraints = {
    // width: { min: 350 },
    // height: { min: 350 },
    aspectRatio: 1,
    facingMode: "user",
    video: {
      advanced: {
        width: 800,
        height: 800
      }
    }
  };
  const webcamRef = useRef<any>();
  const videoRecorderRef = useRef<any>();
  const audioRecorderRef = useRef<any>();
  const [audioRecording, setAudioRecording] = useState<ArrayBuffer>();
  const [videoRecording, setVideoRecording] = useState<ArrayBuffer>();

  // timeout for controlling speech recognition
  const speechTimeout = useRef<NodeJS.Timeout | null>(null);

  // interview state controls
  // const [interviewStarted, setInterviewStarted] = useState<boolean>(false);

  // speech recognition controls
  const {
    finalTranscript,
    resetTranscript,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  // page load
  useEffect(() => {
    // retrieve interview details
    if (interviewId)
      dispatch(candidateRetrieveInterview(interviewId)).unwrap()
        .then(() => {
          // start interview only if interview is valid
          if (interview.status === InterviewStatusEnum.ACTIVE) {
            startInterview();
          } else {
            console.log("Interview is not active, redirecting to error page.");
            history.push("/error")
          }
        })
        .catch(() => {
          // if interview not found, redirect out!
          console.log("Failed to validate interview, please check resume token and interview id.")
          history.push("/error")
        })
  }, []);

  // listen to speech recognition api after user finish speaking and final transcript is produced
  useEffect(() => {
    console.log('final transcript', finalTranscript);

    // the purpose of using speech recognition api is to ensure the user speak something
    if ('' !== finalTranscript.trim()) {
      // clear timeout if it exists
      if (speechTimeout.current) {
        clearTimeout(speechTimeout.current);
      }

      // set timeout to stop listening after 4 seconds
      speechTimeout.current = setTimeout(() => {
        console.log('stop listening');

        // set state
        dispatch(setRecognizing(false));

        // turn off listening in speech recognition API
        SpeechRecognition.stopListening();

        // stop capture!
        triggerStopCapture();

        // reset transcript
        resetTranscript();
      }, 2000);
    }
  }, [finalTranscript]);

  useEffect(() => {
    if (started) {
      // start recording when bot response completes
      if (!loading && !playing) {
        console.log('listening');

        // set state
        dispatch(setRecognizing(true));

        // use speech recognition API to detect when the person finish talking
        SpeechRecognition.startListening({ continuous: true, language: 'en-GB' });

        // start recording audio and video stream
        triggerStartCapture();
      }
    }
  }, [loading, playing]);

  // handle when the interview ends
  useEffect(() => {
    if (ended) {
      stopInterview();
    }
  }, [ended]);

  // handle when the audio and video recordings are populated
  useEffect(() => {
    (async () => {
      // wait for interview recording base64 to be populated after stopping media stream
      if (audioRecording && videoRecording) {
        // console.log('timestamp', timestamp);
        // console.log('upload url', [audioUploadUrl, videoUploadUrl]);

        // ensure that all information are populated
        if (audioRecording && speechResponse.audioUploadUrl &&
          videoRecording && speechResponse.videoUploadUrl) {
          console.log('uploading to s3');

          // wait for audio and video to finish uploading
          const response = await Promise.all([
            uploadMediaToS3(audioRecording, 'video/webm', speechResponse.audioUploadUrl),
            uploadMediaToS3(videoRecording, 'video/webm', speechResponse.videoUploadUrl),
          ]);
          console.log(response);

          // send to chat
          chat();
        }
      }
    })();
  }, [audioRecording, videoRecording]);

  // trigger back-end call to init and reply to chat
  const chat = () => {
    // retrieve chatbot details
    dispatch(candidateInterviewBot(interviewId)).unwrap()
      .catch(() => {
        // error retrieving chatbot details, redirect out!
        history.push("/interview/" + interviewId)
      })
  }

  // trigger start of interview (when start button is click)
  const startInterview = () => {
    if (isMicrophoneAvailable && webcamRef.current) {
      chat();
    }
  }

  // trigger stop of interview (force stop)
  const stopInterview = async () => {
    // setInterviewStarted(false);
    triggerStopCapture();
  }

  // utility method to upload media to S3
  const uploadMediaToS3 = async (media: ArrayBuffer, contentType: string, uploadUrl: string) => {
    return dispatch(candidateUploadS3({media, contentType, uploadUrl}));
  }

  /*
  *  audio and video methods
  */

   // trigger the start of recording audio and video stream
  const triggerStartCapture = useCallback(async () => {
  const audioContext = new AudioContext();
  const destAudioContext = audioContext.createMediaStreamDestination();

  // record audio from mic and system
  Promise.all([
    navigator.mediaDevices.getUserMedia({ audio: true }),
    // navigator.mediaDevices.getDisplayMedia({
    //   audio: true,
    //   video: false
    // }),
  ])
    .then(([micStream]) => {
      // merge both streams
      [micStream].forEach((str) => {
        const src = audioContext.createMediaStreamSource(str);
        src.connect(destAudioContext);
      });

      // record audio!
      audioRecorderRef.current = new MediaRecorder(destAudioContext.stream, {
        mimeType: "audio/mp4;codecs=opus",
      });
      audioRecorderRef.current.addEventListener("dataavailable", handleAudioStream);
      audioRecorderRef.current.start();
    });

    // record video from webcam
    videoRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/mp4;codecs=opus"
    });
    videoRecorderRef.current.addEventListener("dataavailable", handleVideoStream);
    videoRecorderRef.current.start();

    console.log('recording started');
  }, [audioRecorderRef, videoRecorderRef]);

  // handle the recording of audio stream
  const handleAudioStream = useCallback(
    ({ data }: { data: any }) => {
      if (data.size > 0) {
        setAudioRecording(data);
      }
    },
    [setAudioRecording]
  );

  // handle the recording of video stream
  const handleVideoStream = useCallback(
    ({ data }: { data: any }) => {
      if (data.size > 0) {
        setVideoRecording(data);
      }
    },
    [setVideoRecording]
  );

  // trigger the stop of the audio and video capture
  const triggerStopCapture = useCallback(() => {
    audioRecorderRef.current.stop();
    videoRecorderRef.current.stop();
  }, [videoRecorderRef, audioRecorderRef]);

  return (
    <div className={styles.chatbot}>
      <div className={`${styles.gridWrapper} grid`}>
        {/* Left Panel: Avatar & Webcam */}
        <div className="col-12 md:col-3">
          <Card className="p-0">
            <div className="grid overflow-hidden">
              <div id="chatAvatar"
                className="col-6 md:col-12 flex justify-content-center"
                style={{ 
                  backgroundImage: `url('data:image/png;base64,${interview.tenant.interviewerBackgroundImage}')`
                 }}>
                <Canvas
                  shadows
                  camera={{ position: [0, 0, 0], fov: 8 }}
                  style={{ width: 300, height: 300 }}
                >
                  <Experience 
                    interviewerAvatar={interview.tenant.interviewerAvatar} />
                </Canvas>
              </div>
              <div className="col-6 md:col-12 flex justify-content-center">
                <Webcam
                  audio={true}
                  muted={true}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  width={300}
                  height={300}
                />
              </div>
            </div>
          </Card>
        </div>
        {/* Right Panel: Chat & Status */}
        <div className="col-12 md:col-9">
          <Card className="">
            <div className="flex align-items-center pb-3">
              <div className="relative flex align-items-center mr-3">
                <Avatar 
                  image={`https://xi-docs.s3.ap-southeast-1.amazonaws.com/${interview.tenant.interviewerAvatar.toLowerCase()}.jpg`}
                  shape="circle" 
                  size="xlarge" />
              </div>
              <div className="mr-2">
                <span className="text-xl font-semibold block">{interview.tenant.interviewerName}</span>
                <span className="text-700">Your friendly interview bot.</span>
              </div>
            </div>

            {loading &&
              <div>
                <Divider />
                <div className="flex justify-content-center align-items-center pt-2">
                  <i className="pi pi-spin pi-spinner mr-2"></i> {interview.tenant.interviewerName} is thinking...
                </div>
              </div>}

            {started && !loading &&
              <div>
                <Divider />
                <div className="flex mb-4 align-items-start">
                  <div className="mr-3 mt-1">
                    <Avatar 
                      image={`https://xi-docs.s3.ap-southeast-1.amazonaws.com/${interview.tenant.interviewerAvatar.toLowerCase()}.jpg`}
                      shape="circle" 
                      size="large" />
                  </div>
                  <div className="flex-1 mt-3">
                    <div className="text-900 font-semibold mb-3">{interview.tenant.interviewerName}</div>
                    <div className="text-700 inline-block font-medium border-1 surface-border p-3 white-space-normal border-round" style={{ whiteSpace: 'pre-line', maxWidth: '100%' }}>
                      {speechResponse.content}
                    </div>
                  </div>
                </div>
              </div>}

            {started && !ended && recognizing &&
              <div>
                <div className="flex justify-content-end mt-3">
                  <span className="inline-block text-left font-medium border-1 surface-border bg-primary-100 text-primary-900 p-3 white-space-normal border-round" style={{ whiteSpace: 'pre-line', maxWidth: '80%' }}>
                    <i className="pi pi-microphone mr-2"></i> It's your turn to start talking
                  </span>
                </div>
              </div>}

            {ended &&
              <div>
                <Divider />
                <div className="flex justify-content-center align-items-center pt-2">
                  <i className="pi pi-check-circle mr-2"></i> That concludes the interview, you can now close this window.
                </div>
              </div>}
          </Card>
        </div>
      </div>


      {/* <Loader /> */}
      <Leva hidden={true} />
      {/* <div className="grid">
        <div className="col m-4">
          <UI hidden={false} />
        </div>
        <div className="col-fixed m-4">
        </div>
      </div> */}
    </div>
  );
}

export default InterviewBot